import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export type TrainingBreadcrumbMode =
  | 'course'
  | 'create-course'
  | 'classroom'
  | 'create-classroom'
  | 'classroom-run'
  | 'assignment'
  | 'edit-assignment'
  | 'session'
  | 'user-session'
  | 'certificate'
  | 'user-certificate'
  | 'user-course'
  | 'user-session-tracker'
  | 'sign-off-detail'
  | 'sign-off-evaluate'
  | 'course-video'
  | 'create-course-video'
  | 'edit-course-video';

export interface UseTrainingBreadcrumbsProps {
  mode: TrainingBreadcrumbMode;
  definitionId?: string;
  courseName?: string;
  classroomId?: string;
  classroomIdentifier?: string;
  assignmentId?: string;
  assigneeName?: string;
  sessionId?: string;
  sessionIdentifier?: string;
  videoId?: string | number;
  videoTitle?: string;
  signOffSubject?: string;
}

export interface UseTrainingBreadcrumbsResult {
  breadcrumbs: BreadcrumbItem[];
  fallbackTo: string;
  fallbackLabel: string;
}

interface CrumbTarget {
  label: string;
  path: string;
}

/** Per-mode trail shape; the nearest linked crumb is the Back fallback. */
interface ModeSpec {
  root: CrumbTarget;
  parent?: CrumbTarget;
  tail: BreadcrumbItem[];
}

const TRAINING_ROOT: CrumbTarget = {
  label: 'Training',
  path: PathUtility.TrainingDashboard('manage'),
};
const MY_TRAINING_ROOT: CrumbTarget = {
  label: 'My Training',
  path: PathUtility.TrainingDashboard('user'),
};
const COURSE_VIDEO_ROOT: CrumbTarget = {
  label: 'Training',
  path: PathUtility.TrainingDashboard('course-video'),
};

export const useTrainingBreadcrumbs = ({
  mode,
  definitionId,
  courseName,
  classroomId,
  classroomIdentifier,
  assignmentId,
  assigneeName,
  sessionId,
  sessionIdentifier,
  videoId,
  videoTitle,
  signOffSubject,
}: UseTrainingBreadcrumbsProps): UseTrainingBreadcrumbsResult => {
  return useMemo((): UseTrainingBreadcrumbsResult => {
    const courseLabel = courseName || 'Course';
    const assigneeLabel = assigneeName || 'Assignment';

    const courseParent = (
      view: 'roster' | 'classes' | 'assign'
    ): CrumbTarget => ({
      label: courseLabel,
      path: PathUtility.TrainingCourseView(definitionId || '', view),
    });

    // Lazy: a mode must only build the paths it actually uses.
    const assignmentParent = (): CrumbTarget => ({
      label: assigneeLabel,
      path: PathUtility.TrainingCourseAssignmentView(
        definitionId || '',
        assignmentId || '',
        'content'
      ),
    });

    const signOffsParent = (): CrumbTarget => ({
      label: 'Sign-offs',
      path: PathUtility.TrainingSignOffs(),
    });

    const spec = ((): ModeSpec => {
      switch (mode) {
        case 'course':
          return {root: TRAINING_ROOT, tail: [{label: courseLabel}]};

        case 'create-course':
          return {root: TRAINING_ROOT, tail: [{label: 'New Course'}]};

        case 'classroom':
          return {
            root: TRAINING_ROOT,
            parent: courseParent('classes'),
            tail: [
              {
                label: classroomIdentifier
                  ? `Classroom ${classroomIdentifier}`
                  : 'Classroom',
              },
            ],
          };

        case 'create-classroom':
          return {
            root: TRAINING_ROOT,
            parent: courseParent('classes'),
            tail: [{label: 'New Classroom'}],
          };

        case 'classroom-run': {
          const classroom: CrumbTarget = {
            label: classroomIdentifier
              ? `Classroom ${classroomIdentifier}`
              : 'Classroom',
            path: PathUtility.TrainingCourseClassroomView(
              definitionId || '',
              classroomId || '',
              'roster'
            ),
          };
          return {
            root: TRAINING_ROOT,
            parent: courseParent('classes'),
            tail: [
              {label: classroom.label, to: classroom.path},
              {label: 'Run'},
            ],
          };
        }

        case 'assignment':
          return {
            root: TRAINING_ROOT,
            parent: courseParent('roster'),
            tail: [{label: assigneeLabel}],
          };

        case 'edit-assignment': {
          const assignment = assignmentParent();
          return {
            root: TRAINING_ROOT,
            parent: courseParent('roster'),
            tail: [
              {label: assignment.label, to: assignment.path},
              {label: 'Edit'},
            ],
          };
        }

        case 'session':
          return {
            root: TRAINING_ROOT,
            parent: assignmentParent(),
            tail: [{label: sessionIdentifier || 'Session'}],
          };

        case 'certificate': {
          const session: CrumbTarget = {
            label: sessionIdentifier || 'Session',
            path: PathUtility.TrainingCourseSessionView(
              definitionId || '',
              assignmentId || '',
              sessionId || '',
              'overview'
            ),
          };
          return {
            root: TRAINING_ROOT,
            parent: assignmentParent(),
            tail: [
              {label: session.label, to: session.path},
              {label: 'Certificate'},
            ],
          };
        }

        case 'user-course':
          return {root: MY_TRAINING_ROOT, tail: [{label: courseLabel}]};

        case 'user-session':
          return {
            root: MY_TRAINING_ROOT,
            parent: {
              label: courseLabel,
              path: PathUtility.TrainingCourseOverview(assignmentId || ''),
            },
            tail: [
              {
                label: sessionIdentifier
                  ? `Session ${sessionIdentifier}`
                  : 'Session',
              },
            ],
          };

        case 'user-session-tracker':
          return {
            root: MY_TRAINING_ROOT,
            parent: assignmentId
              ? {
                  label: courseLabel,
                  path: PathUtility.TrainingCourseOverview(assignmentId),
                }
              : undefined,
            tail: [{label: 'Session'}],
          };

        case 'user-certificate':
          return {
            root: MY_TRAINING_ROOT,
            parent: sessionId
              ? {
                  label: sessionIdentifier
                    ? `Session ${sessionIdentifier}`
                    : 'Session',
                  path: PathUtility.TrainingSessionRecord(
                    sessionId,
                    'overview'
                  ),
                }
              : {
                  label: courseLabel,
                  path: PathUtility.TrainingCourseOverview(assignmentId || ''),
                },
            tail: [{label: 'Certificate'}],
          };

        case 'sign-off-detail':
          return {
            root: TRAINING_ROOT,
            parent: signOffsParent(),
            tail: [
              {
                label: signOffSubject
                  ? `${signOffSubject} — Sign-off`
                  : 'Sign-off',
              },
            ],
          };

        case 'sign-off-evaluate':
          return {
            root: TRAINING_ROOT,
            parent: signOffsParent(),
            tail: [{label: 'Evaluate'}],
          };

        case 'course-video':
          return {
            root: COURSE_VIDEO_ROOT,
            tail: [{label: videoTitle || 'Course Video'}],
          };

        case 'create-course-video':
          return {root: COURSE_VIDEO_ROOT, tail: [{label: 'New Course Video'}]};

        case 'edit-course-video': {
          const video: CrumbTarget = {
            label: videoTitle || 'Course Video',
            path: PathUtility.CourseVideoView(String(videoId || ''), 'content'),
          };
          return {
            root: COURSE_VIDEO_ROOT,
            tail: [{label: video.label, to: video.path}, {label: 'Edit'}],
          };
        }
      }
    })();

    const breadcrumbs: BreadcrumbItem[] = [
      {label: spec.root.label, to: spec.root.path},
      ...(spec.parent
        ? [{label: spec.parent.label, to: spec.parent.path}]
        : []),
      ...spec.tail,
    ];
    const fallback = breadcrumbs.reduce<CrumbTarget>(
      (nearest, crumb) =>
        crumb.to ? {label: crumb.label, path: crumb.to} : nearest,
      spec.root
    );

    return {
      breadcrumbs,
      fallbackTo: fallback.path,
      fallbackLabel: `Back to ${fallback.label}`,
    };
  }, [
    mode,
    definitionId,
    courseName,
    classroomId,
    classroomIdentifier,
    assignmentId,
    assigneeName,
    sessionId,
    sessionIdentifier,
    videoId,
    videoTitle,
    signOffSubject,
  ]);
};
